import { supabase } from '../supabaseClient';

// Harvest API types
export interface HarvestEstimate {
  id: number;
  client_id: number;
  client_name: string;
  number: string;
  subject: string;
  notes: string;
  currency: string;
  state: 'draft' | 'sent' | 'accepted' | 'declined';
  issue_date: string;
  amount: number;
  tax: number;
  tax_amount: number;
  tax2: number;
  tax2_amount: number;
  discount: number;
  discount_amount: number;
  line_items: HarvestLineItem[];
}

export interface HarvestLineItem {
  id: number;
  kind: 'Service' | 'Product';
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  taxed: boolean;
  taxed2: boolean;
}

export interface HarvestConnection {
  id: string;
  userId: string;
  connectionType: 'harvest';
  connectionName: string;
  config: {
    accountId?: string;
    baseUrl?: string;
  };
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
  status: 'active' | 'inactive' | 'error' | 'pending_auth';
  lastSyncAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

class HarvestService {
  private readonly HARVEST_API_BASE = 'https://api.harvestapp.com/v2';
  private readonly HARVEST_OAUTH_URL = 'https://id.getharvest.com/oauth2/authorize';
  private readonly HARVEST_TOKEN_URL = 'https://id.getharvest.com/api/v2/oauth2/token';
  
  /**
   * Start OAuth flow for Harvest
   */
  async startOAuthFlow(): Promise<void> {
    const clientId = import.meta.env.VITE_HARVEST_CLIENT_ID;
    const redirectUri = `${window.location.origin}/settings/connections/harvest/callback`;
    
    if (!clientId) {
      throw new Error('Harvest Client ID not configured');
    }
    
    const state = this.generateRandomState();
    sessionStorage.setItem('harvest_oauth_state', state);
    
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      state,
      response_type: 'code',
    });
    
    window.location.href = `${this.HARVEST_OAUTH_URL}?${params.toString()}`;
  }
  
  /**
   * Complete OAuth flow and save connection
   */
  async completeOAuthFlow(code: string, state: string): Promise<HarvestConnection> {
    const savedState = sessionStorage.getItem('harvest_oauth_state');
    
    if (state !== savedState) {
      throw new Error('Invalid OAuth state');
    }
    
    // Exchange code for tokens via Edge Function (to keep client_secret secure)
    const { data, error } = await supabase.functions.invoke('harvest-oauth', {
      body: { code, redirectUri: `${window.location.origin}/settings/connections/harvest/callback` }
    });
    
    if (error) {
      console.error('Harvest OAuth error:', error);
      throw error;
    }
    
    if (!data || !data.access_token) {
      console.error('Invalid OAuth response:', data);
      throw new Error('Failed to get access token from Harvest');
    }
    
    console.log('🔍 Full Harvest OAuth response:', data);
    console.log('🔍 Response keys:', Object.keys(data));
    
    const { access_token, refresh_token, expires_in, token_type } = data;
    
    // Harvest OAuth response includes account info
    const accountId = data.account?.id;
    const accountName = data.account?.name || data.account?.product;
    
    console.log('🔍 Account from token:', { accountId, accountName });
    
    // OAuth tokens don't include account info - we must fetch it from Harvest ID API
    // This is REQUIRED for v2 API calls (they need Harvest-Account-Id header)
    const accountInfo = await this.getAccountInfo(access_token);
    const finalAccountId = accountInfo.id;
    const finalAccountName = accountInfo.name;
    
    console.log('✅ Got account info:', { finalAccountId, finalAccountName });
    
    // Save connection to database
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + expires_in);
    
    const { data: connection, error: insertError } = await supabase
      .from('connections')
      .upsert({
        user_id: user.id,
        connection_type: 'harvest',
        connection_name: `Harvest - ${finalAccountName}`,
        config: {
          accountId: finalAccountId,
          company: finalAccountName,
          baseUrl: 'https://api.harvestapp.com/v2',
        },
        oauth_provider: 'harvest',
        access_token,
        refresh_token,
        token_expires_at: expiresAt.toISOString(),
        status: 'active',
      })
      .select()
      .single();
    
    if (insertError) throw insertError;
    
    sessionStorage.removeItem('harvest_oauth_state');
    
    return this.transformConnection(connection);
  }
  
  /**
   * Get Harvest connection for current user
   */
  async getConnection(): Promise<HarvestConnection | null> {
    const { data, error } = await supabase
      .from('connections')
      .select('*')
      .eq('connection_type', 'harvest')
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null; // No connection found
      throw error;
    }
    
    return this.transformConnection(data);
  }
  
  /**
   * Delete Harvest connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await supabase
      .from('connections')
      .delete()
      .eq('id', connectionId);
    
    if (error) throw error;
  }
  
  /**
   * Fetch approved estimates from Harvest
   * @param connectionId - Connection ID (not used, kept for compatibility)
   * @param harvestClientId - Optional Harvest client ID to filter estimates
   */
  async fetchApprovedEstimates(connectionId: string, harvestClientId?: string): Promise<HarvestEstimate[]> {
    const connection = await this.getConnection();
    if (!connection) throw new Error('No Harvest connection found');
    
    const accessToken = await this.getValidAccessToken(connection);
    
    // Harvest v2 API REQUIRES the Harvest-Account-Id header
    if (!connection.config.accountId) {
      throw new Error('No Harvest Account ID found - please reconnect to Harvest');
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${accessToken}`,
      'Harvest-Account-Id': String(connection.config.accountId),
      'User-Agent': 'Klarinet CLM (matt@klarinet.com)',
    };
    
    console.log('📤 Fetching estimates with Account ID:', connection.config.accountId);
    if (harvestClientId) {
      console.log('📤 Filtering by Harvest client ID:', harvestClientId);
    }
    
    // Build URL with optional client filter
    let url = `${this.HARVEST_API_BASE}/estimates?state=accepted&per_page=100`;
    if (harvestClientId) {
      url += `&client_id=${harvestClientId}`;
    }
    
    // Fetch estimates with state=accepted
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const error = await response.text();
      console.error('Harvest API error:', error);
      throw new Error(`Harvest API error: ${error}`);
    }
    
    const data = await response.json();
    
    console.log('📊 Raw Harvest estimates response:', data);
    console.log('📊 First estimate structure:', data.estimates?.[0]);
    
    // Cache estimates in database
    await this.cacheEstimates(connectionId, data.estimates);
    
    // Update last sync time
    await supabase
      .from('connections')
      .update({ 
        last_sync_at: new Date().toISOString(),
        status: 'active',
        last_error: null,
      })
      .eq('id', connectionId);
    
    return data.estimates;
  }
  
  /**
   * Get cached estimates from database
   */
  async getCachedEstimates(state?: string): Promise<any[]> {
    let query = supabase
      .from('harvest_estimates')
      .select('*')
      .order('issue_date', { ascending: false });
    
    if (state) {
      query = query.eq('state', state);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data || [];
  }

  /**
   * Fetch contacts for a specific Harvest client
   */
  async fetchHarvestClientContacts(harvestClientId: string): Promise<any[]> {
    const connection = await this.getConnection();
    if (!connection) throw new Error('No Harvest connection found');
    
    const accessToken = await this.getValidAccessToken(connection);
    
    if (!connection.config.accountId) {
      throw new Error('No Harvest Account ID found - please reconnect to Harvest');
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${accessToken}`,
      'Harvest-Account-Id': String(connection.config.accountId),
      'User-Agent': 'Klarinet CLM (matt@klarinet.com)',
    };
    
    console.log(`📤 Fetching contacts for Harvest client ${harvestClientId}...`);
    
    const response = await fetch(
      `${this.HARVEST_API_BASE}/contacts?client_id=${harvestClientId}&per_page=100`,
      { headers }
    );
    
    if (!response.ok) {
      const error = await response.text();
      console.error('Harvest API error:', error);
      throw new Error(`Harvest API error: ${error}`);
    }
    
    const data = await response.json();
    console.log(`📊 Fetched ${data.contacts?.length || 0} contacts for client ${harvestClientId}`);
    
    return data.contacts || [];
  }

  /**
   * List clients from Harvest (public API for UI)
   */
  async listClients(): Promise<{ clients: any[] }> {
    const clients = await this.fetchHarvestClients();
    return { clients };
  }

  /**
   * Fetch clients from Harvest API (all pages)
   */
  async fetchHarvestClients(): Promise<any[]> {
    const connection = await this.getConnection();
    if (!connection) throw new Error('No Harvest connection found');
    
    const accessToken = await this.getValidAccessToken(connection);
    
    // Harvest v2 API REQUIRES the Harvest-Account-Id header
    if (!connection.config.accountId) {
      throw new Error('No Harvest Account ID found - please reconnect to Harvest');
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${accessToken}`,
      'Harvest-Account-Id': String(connection.config.accountId),
      'User-Agent': 'LightPath CLM (matt@klarinet.com)',
    };
    
    console.log('📤 Fetching clients from Harvest...');
    
    // Fetch all pages of active clients
    let allClients: any[] = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      const response = await fetch(
        `${this.HARVEST_API_BASE}/clients?is_active=true&per_page=100&page=${page}`,
        { headers }
      );
      
      if (!response.ok) {
        const error = await response.text();
        console.error('Harvest API error:', error);
        throw new Error(`Harvest API error: ${error}`);
      }
      
      const data = await response.json();
      allClients = [...allClients, ...(data.clients || [])];
      
      console.log(`📊 Fetched page ${page}/${data.total_pages}: ${data.clients?.length} clients`);
      
      // Check if there are more pages
      hasMore = page < data.total_pages;
      page++;
    }
    
    console.log(`✅ Total clients fetched: ${allClients.length}`);
    return allClients;
  }
  
  /**
   * Get single estimate by ID
   */
  async getEstimate(estimateId: string): Promise<any> {
    const { data, error } = await supabase
      .from('harvest_estimates')
      .select('*')
      .eq('id', estimateId)
      .single();
    
    if (error) throw error;
    return data;
  }
  
  /**
   * Mark estimate as imported
   */
  async markEstimateAsImported(estimateId: string, contractId: string): Promise<void> {
    const { error } = await supabase
      .from('harvest_estimates')
      .update({
        contract_id: contractId,
        imported_at: new Date().toISOString(),
      })
      .eq('id', estimateId);
    
    if (error) throw error;
  }
  
  /**
   * Test connection
   */
  async testConnection(connectionId: string): Promise<boolean> {
    try {
      const connection = await this.getConnection();
      if (!connection) return false;
      
      const accessToken = await this.getValidAccessToken(connection);
      
      const response = await fetch(`${this.HARVEST_API_BASE}/users/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Harvest-Account-Id': connection.config.accountId!,
          'User-Agent': 'Klarinet CLM (matt@klarinet.com)',
        },
      });
      
      return response.ok;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
  
  // Private helpers
  
  private async getValidAccessToken(connection: HarvestConnection): Promise<string> {
    // Check if token is expired
    if (connection.tokenExpiresAt) {
      const expiresAt = new Date(connection.tokenExpiresAt);
      const now = new Date();
      
      // Refresh if expired or expiring in next 5 minutes
      if (expiresAt.getTime() - now.getTime() < 5 * 60 * 1000) {
        return await this.refreshAccessToken(connection);
      }
    }
    
    return connection.accessToken!;
  }
  
  private async refreshAccessToken(connection: HarvestConnection): Promise<string> {
    const { data, error } = await supabase.functions.invoke('harvest-refresh-token', {
      body: { refresh_token: connection.refreshToken }
    });
    
    if (error) {
      await this.markConnectionError(connection.id, 'Failed to refresh token');
      throw error;
    }
    
    const { access_token, refresh_token, expires_in } = data;
    
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + expires_in);
    
    // Update connection with new tokens
    await supabase
      .from('connections')
      .update({
        access_token,
        refresh_token,
        token_expires_at: expiresAt.toISOString(),
        status: 'active',
        last_error: null,
      })
      .eq('id', connection.id);
    
    return access_token;
  }
  
  private async getAccountInfo(accessToken: string): Promise<any> {
    // After OAuth, we need to call the Harvest ID API (v1) to get account IDs
    // Then we use those IDs in the Harvest-Account-Id header for v2 API calls
    console.log('🔍 Fetching accounts from Harvest ID API...');
    
    const accountsResponse = await fetch('https://id.getharvest.com/api/v1/accounts', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
      },
    });
    
    if (!accountsResponse.ok) {
      const errorText = await accountsResponse.text();
      console.error('Failed to get Harvest accounts:', errorText);
      throw new Error('Failed to get Harvest account info');
    }
    
    const accountsData = await accountsResponse.json();
    console.log('Harvest ID API response:', accountsData);
    
    // Response format: { user: {...}, accounts: [{id, name, product}, ...] }
    if (accountsData.accounts && accountsData.accounts.length > 0) {
      // Find the first Harvest account (not Forecast)
      const harvestAccount = accountsData.accounts.find((acc: any) => acc.product === 'harvest') 
                            || accountsData.accounts[0];
      
      console.log('Selected Harvest account:', harvestAccount);
      
      return {
        id: harvestAccount.id,
        name: harvestAccount.name,
        company: harvestAccount.name,
        base_uri: 'https://api.harvestapp.com/v2',
      };
    }
    
    throw new Error('No Harvest accounts found for this user');
  }
  
  private async cacheEstimates(connectionId: string, estimates: HarvestEstimate[]): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const records = estimates.map(est => ({
      connection_id: connectionId,
      user_id: user.id,
      harvest_estimate_id: est.id,
      estimate_number: est.number,
      // Handle both nested client object and flat structure
      client_name: est.client?.name || est.client_name || 'Unknown Client',
      client_id: est.client?.id || est.client_id,
      subject: est.subject,
      notes: est.notes,
      currency: est.currency,
      state: est.state,
      issue_date: est.issue_date,
      amount: est.amount,
      tax: est.tax,
      tax_amount: est.tax_amount,
      tax2: est.tax2,
      tax2_amount: est.tax2_amount,
      discount: est.discount,
      discount_amount: est.discount_amount,
      line_items: est.line_items,
      raw_data: est,
      synced_at: new Date().toISOString(),
    }));
    
    // Upsert estimates (update if exists, insert if new)
    const { error } = await supabase
      .from('harvest_estimates')
      .upsert(records, { 
        onConflict: 'connection_id,harvest_estimate_id',
        ignoreDuplicates: false 
      });
    
    if (error) {
      console.error('Failed to cache estimates:', error);
    }
  }
  
  private async markConnectionError(connectionId: string, errorMessage: string): Promise<void> {
    await supabase
      .from('connections')
      .update({
        status: 'error',
        last_error: errorMessage,
      })
      .eq('id', connectionId);
  }
  
  private transformConnection(row: any): HarvestConnection {
    return {
      id: row.id,
      userId: row.user_id,
      connectionType: row.connection_type,
      connectionName: row.connection_name,
      config: row.config,
      accessToken: row.access_token,
      refreshToken: row.refresh_token,
      tokenExpiresAt: row.token_expires_at,
      status: row.status,
      lastSyncAt: row.last_sync_at,
      lastError: row.last_error,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
  
  private generateRandomState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
}

export const harvestService = new HarvestService();

